import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type { ActivatedRouteSnapshot, Params, Resolve, RouterStateSnapshot } from '@angular/router';
import type { Observable } from 'rxjs';
import { of, throwError } from 'rxjs';
import { catchError, concatMap, take } from 'rxjs/operators';

import { WidgetsService } from 'components/organisms/widgets-loader/widgets.service';
import { WIDGETS_CONFIG } from 'components/widgets/widgets-config';
import { HTTP_ERROR_HANDLER_SERVICE } from 'src/services/http-error-handler/entities/const';
import type { IHttpErrorHandlerService } from 'src/services/http-error-handler/entities/types';
import { TypicalProjectsRestService } from 'src/services/typical-projects-rest/typical-projects-rest.service';

import { BaseResolver } from './base-resolver';
import type { IDefaultLayout, ILandingPage } from '../layouts/entities/types';

@Injectable()
export class ViewResolverService extends BaseResolver implements Resolve<ILandingPage | IDefaultLayout> {
    protected readonly httpErrorHandlerService: IHttpErrorHandlerService;
    private readonly restService = inject(TypicalProjectsRestService);
    private readonly widgetsService = inject(WidgetsService);

    private readonly technicalPages = ['guides-category-page', 'guides-tag-page'];

    constructor() {
        const httpErrorHandlerService = inject<IHttpErrorHandlerService>(HTTP_ERROR_HANDLER_SERVICE);

        super(httpErrorHandlerService);

        this.httpErrorHandlerService = httpErrorHandlerService;
    }

    public resolve(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<ILandingPage | IDefaultLayout> {
        if (!this.isUrlPathCorrect(state)) {
            return this.handleError(new HttpErrorResponse({ status: 404 }));
        }

        return this.makePageRequest(route.params).pipe(
            take(1),
            concatMap((data) => this.handleSuccess(data)),
            concatMap((data) => this.widgetsService.resolveWidgetsFromPage(data, WIDGETS_CONFIG)),
            concatMap((data) => this.checkSubdirectory(data, route)),
            catchError((error) => this.handleError(error))
        );
    }

    private makePageRequest(routeParams: Params) {
        const { rootSlug, projectSlug, pageSlug, subDir } = routeParams; // don't delete subDir

        if (this.technicalPages.indexOf(pageSlug) !== -1) {
            return this.hideTechnicalPages();
        }

        if ([rootSlug, projectSlug, pageSlug].some(Boolean)) {
            return rootSlug
                ? this.restService.getProjectRootPage(rootSlug)
                : this.restService.getProjectPage(routeParams);
        }

        return this.restService.getHomePage();
    }

    private checkSubdirectory(data: ILandingPage, route): Observable<ILandingPage | never> {
        if (
            (!route.params?.subDir && data.subDirectory) ||
            (route.params?.subDir && route.params.subDir !== data.subDirectory)
        ) {
            return throwError({ status: 404 });
        }

        return of(data);
    }

    private hideTechnicalPages(): Observable<null> {
        this.httpErrorHandlerService.setHttpStatus(404);
        return of(null);
    }
}
